import { useState } from "react";
import { toast } from "sonner";
import { Trash2, UserPlus, BadgeCheck, ArrowLeft } from "lucide-react";
import { api, errMsg, inr } from "@/lib/api";
import { PageHeader, Card, Field, Btn } from "@/components/shared";
import { AddBene, OtpDialog, RecentTransfers, RegisterRemitter, RemitterKyc, TransferDialog, useCall } from "@/pages/DmtParts";

function RemitterView({ data, mobile, reload, tick }) {
  const [addOpen, setAddOpen] = useState(false);
  const [sendTo, setSendTo] = useState(null);
  const [delId, setDelId] = useState(null);
  const { call } = useCall();
  const startDel = async (b) => {
    if (!window.confirm(`Remove beneficiary ${b.name}?`)) return;
    const d = await call("del", () => api.post("/dmt/beneficiary/delete", { remitter_mobile: mobile, bene_id: b.id }));
    if (d?.otp_required) setDelId(b.id); else if (d) { toast.success("Beneficiary removed"); reload(); }
  };
  const confirmDel = async (otp) => { const ok = await call("dv", () => api.post("/dmt/beneficiary/delete-verify", { remitter_mobile: mobile, bene_id: delId, otp }), "Beneficiary removed"); if (ok) { setDelId(null); reload(); } return ok; };
  return (
    <>
      <div className="grid gap-4 md:grid-cols-4" data-testid="remitter-info">
        <Card className="md:col-span-2"><div className="np-label">Remitter</div><div className="font-display text-xl font-bold" data-testid="remitter-name">{data.remitter.name || mobile}</div><div className="text-xs text-slate-500">{mobile} · {data.remitter.city}</div></Card>
        <Card><div className="np-label">Per Transaction</div><div className="font-mono text-xl font-semibold">{inr(data.limit.per_txn)}</div></Card>
        <Card><div className="np-label">Limit Available</div><div className="font-mono text-xl font-semibold text-emerald-700" data-testid="remitter-remaining">{inr(data.limit.available)}</div></Card>
      </div>
      <Card title={`Beneficiaries (${data.beneficiaries.length})`} action={<Btn variant="dark" onClick={() => setAddOpen(true)} data-testid="add-beneficiary-button"><UserPlus className="h-4 w-4" />Add Bene</Btn>}>
        {data.beneficiaries.length === 0 && <div className="py-8 text-center text-sm text-slate-400">No Beneficiary Available</div>}
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {data.beneficiaries.map((b) => (
            <div key={b.id} className="rounded-lg border border-slate-200 p-4" data-testid={`bene-card-${b.id}`}>
              <div className="flex items-start justify-between">
                <div className="font-semibold">{b.name} {b.verified && <BadgeCheck className="inline h-4 w-4 text-emerald-600" />}</div>
                <button onClick={() => startDel(b)} className="text-slate-400 hover:text-red-600" data-testid={`delete-bene-${b.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="mt-1 text-xs text-slate-500">{b.bank}</div>
              <div className="font-mono text-sm">{b.account_no} · {b.ifsc}</div>
              <Btn className="mt-3 h-9 w-full" onClick={() => setSendTo(b)} data-testid={`send-money-${b.id}`}>Send Money</Btn>
            </div>
          ))}
        </div>
      </Card>
      <RecentTransfers mobile={mobile} reload={tick} />
      <AddBene open={addOpen} onClose={() => setAddOpen(false)} mobile={mobile} onDone={reload} />
      <OtpDialog open={!!delId} title="Confirm beneficiary removal" onSubmit={confirmDel} onClose={() => setDelId(null)} testid="bene-del-otp" />
      {sendTo && <TransferDialog bene={sendTo} mobile={mobile} remitter={data} onClose={() => setSendTo(null)} onDone={reload} />}
    </>
  );
}

export default function Dmt() {
  const [mobile, setMobile] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);

  const lookup = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/dmt/remitter", { mobile });
      setData(data);
      setTick((t) => t + 1);
    } catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };

  return (
    <div>
      <PageHeader title="Domestic Money Transfer" subtitle="IMPS / NEFT to any bank account. Up to Rs 5,000 per transaction, Rs 25,000 per month per remitter." />
      {!data ? (
        <Card className="max-w-lg">
          <form onSubmit={lookup} className="flex items-end gap-3" data-testid="remitter-lookup-form">
            <Field label="Remitter Mobile Number" className="flex-1">
              <input className="np-input font-mono" inputMode="numeric" maxLength={10} value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))} required data-testid="remitter-mobile-input" />
            </Field>
            <Btn type="submit" loading={loading} disabled={mobile.length !== 10} data-testid="lookup-remitter-button">Continue</Btn>
          </form>
        </Card>
      ) : (
        <div className="space-y-4">
          <button onClick={() => setData(null)} className="flex items-center gap-1 text-sm font-semibold text-blue-700" data-testid="dmt-back-button"><ArrowLeft className="h-4 w-4" />Change remitter</button>
          {data.state === "register" && <RegisterRemitter mobile={mobile} onDone={lookup} />}
          {data.state === "kyc" && <RemitterKyc mobile={mobile} wadh={data.wadh} message={data.message} onDone={lookup} />}
          {data.state === "ready" && <RemitterView data={data} mobile={mobile} reload={lookup} tick={tick} />}
        </div>
      )}
    </div>
  );
}
